import {
  useQuery,
  useQueries,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import type { UserLanguage } from '@xtreed/academy-shared';
import {
  emailTemplatesService,
  type CreateEmailTemplateDto,
  type EmailTemplate,
} from '@/services/email-templates.service';
import { toast } from 'sonner';
import i18n from '@/i18n';
import { notifyApiError } from '@/lib/api-message';

export const emailTemplateKeys = {
  all: ['email-templates'] as const,
  list: () => [...emailTemplateKeys.all, 'list'] as const,
  detail: (id: string) => [...emailTemplateKeys.all, 'detail', id] as const,
  preview: (slug: string) =>
    [...emailTemplateKeys.all, 'preview', slug] as const,
  events: () => [...emailTemplateKeys.all, 'events'] as const,
  default: (slug: string, language: UserLanguage) =>
    [...emailTemplateKeys.all, 'default', slug, language] as const,
};

export interface EmailTemplateLanguageChange {
  slug: string;
  language: UserLanguage;
  id?: string;
  subject: string;
  htmlContent: string;
}

export function useEmailTemplates() {
  return useQuery({
    queryKey: emailTemplateKeys.list(),
    queryFn: () => emailTemplatesService.findAll(),
  });
}

export function useEmailTemplate(id: string) {
  return useQuery({
    queryKey: emailTemplateKeys.detail(id),
    queryFn: () => emailTemplatesService.findOne(id),
    enabled: !!id,
  });
}

export function useEventCatalog() {
  return useQuery({
    queryKey: emailTemplateKeys.events(),
    queryFn: () => emailTemplatesService.getEventCatalog(),
  });
}

export function useEmailTemplateDefault(
  slug: string | null,
  language: UserLanguage,
) {
  return useQuery({
    queryKey: emailTemplateKeys.default(slug ?? '', language),
    queryFn: () => emailTemplatesService.getDefault(slug as string, language),
    enabled: !!slug,
    retry: false,
  });
}

export function useEmailTemplateDefaults(
  slug: string | null,
  languages: readonly UserLanguage[],
) {
  return useQueries({
    queries: languages.map((language) => ({
      queryKey: emailTemplateKeys.default(slug ?? '', language),
      queryFn: () => emailTemplatesService.getDefault(slug as string, language),
      enabled: !!slug,
      retry: false,
      staleTime: Infinity,
    })),
  });
}

export function useCreateEmailTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (dto: CreateEmailTemplateDto) =>
      emailTemplatesService.create(dto),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: emailTemplateKeys.all });
      toast.success(i18n.t('adminEmails:toasts.created'));
    },
    onError: (error) => {
      notifyApiError(error, i18n.t('adminEmails:toasts.createError'));
    },
  });
}

export function useSaveEmailTemplateLanguages() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (changes: EmailTemplateLanguageChange[]) => {
      const saved: EmailTemplate[] = [];
      for (const change of changes) {
        saved.push(
          change.id
            ? await emailTemplatesService.update(change.id, {
                subject: change.subject,
                htmlContent: change.htmlContent,
              })
            : await emailTemplatesService.createLanguage({
                slug: change.slug,
                language: change.language,
                subject: change.subject,
                htmlContent: change.htmlContent,
              }),
        );
      }
      return saved;
    },
    onSuccess: (_saved, changes) => {
      queryClient.invalidateQueries({ queryKey: emailTemplateKeys.all });
      toast.success(
        changes.some((change) => !change.id)
          ? i18n.t('adminEmails:toasts.created')
          : i18n.t('adminEmails:toasts.updated'),
      );
    },
    onError: (error) => {
      queryClient.invalidateQueries({ queryKey: emailTemplateKeys.all });
      notifyApiError(error, i18n.t('adminEmails:toasts.saveError'));
    },
  });
}

export function useResetEmailTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => emailTemplatesService.resetToDefault(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: emailTemplateKeys.all });
      toast.success(i18n.t('adminEmails:toasts.restored'));
    },
    onError: (error) => {
      notifyApiError(error, i18n.t('adminEmails:toasts.restoreError'));
    },
  });
}

export function useUpdateEmailTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      ...dto
    }: {
      id: string;
      subject?: string;
      htmlContent?: string;
      textContent?: string;
      isActive?: boolean;
    }) => emailTemplatesService.update(id, dto),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: emailTemplateKeys.all });
      toast.success(i18n.t('adminEmails:toasts.updated'));
    },
    onError: (error) => {
      notifyApiError(error, i18n.t('adminEmails:toasts.updateError'));
    },
  });
}

export function usePreviewEmailTemplate() {
  return useMutation({
    mutationFn: ({
      slug,
      variables,
      language,
    }: {
      slug: string;
      variables?: Record<string, string>;
      language?: UserLanguage;
    }) => emailTemplatesService.preview(slug, variables, language),
  });
}
